/**
 * Monte Carlo Financial Chart Diagnostic System
 * Based on evidence-based visualization debugging with financial domain specialization
 */

import { SimulationResult } from '@/types/adt';
import { EnhancedMonteCarloResult } from '@/hooks/useEnhancedMonteCarloWorker';

export interface DiagnosticInput {
  // Simulation Context
  simulationContext: {
    iterations: number;
    confidenceLevels: string[];
    simulationEngineVersion?: string;
    financialScenarioParams: Record<string, any>;
  };
  
  // Chart Library Stack
  chartStack: {
    rechartVersion?: string;
    reactVersion?: string;
    chartType: 'fan' | 'histogram' | 'timeline' | 'percentile';
    performanceMode?: boolean;
  };
  
  // Financial Data Package
  simulationResult: SimulationResult | EnhancedMonteCarloResult;
  
  // Evidence Package
  evidence: {
    screenshots?: string[];
    consoleLogs?: string[];
    performanceMetrics?: Record<string, number>;
    accessibilityAudit?: Record<string, any>;
  };
  
  // Expected Financial Message
  expectedMessage: string;
  acceptanceCriteria: string[];
}

export interface DiagnosticFinding {
  layer: 'simulation-data' | 'financial-encoding' | 'temporal-axes' | 'interactive-state' | 'accessibility' | 'performance';
  severity: 'critical' | 'high' | 'medium' | 'low';
  likelihood: 'certain' | 'likely' | 'possible' | 'unlikely';
  evidence: string;
  impact: string;
  fix: string;
  codeReference?: string;
}

export interface DiagnosticReport {
  findings: DiagnosticFinding[];
  reproducerCode: string;
  patches: Array<{
    file: string;
    diff: string;
    description: string;
  }>;
  tests: Array<{
    type: 'unit' | 'accessibility' | 'performance' | 'regression';
    code: string;
    description: string;
  }>;
  accessibilitySummary: string;
  ciGuardrails: string[];
}

export class MonteCarloChartDiagnostics {
  
  /**
   * Main diagnostic entry point
   */
  static diagnose(input: DiagnosticInput): DiagnosticReport {
    const findings: DiagnosticFinding[] = [];
    
    // Layer 1: Simulation Data Integrity
    findings.push(...this.diagnoseSimulationData(input));
    
    // Layer 2: Financial Encoding/Scales
    findings.push(...this.diagnoseFinancialEncoding(input));
    
    // Layer 3: Temporal Axes/Labels
    findings.push(...this.diagnoseTemporalAxes(input));
    
    // Layer 4: Interactive Financial State
    findings.push(...this.diagnoseInteractiveState(input));
    
    // Layer 5: Financial Accessibility
    findings.push(...this.diagnoseAccessibility(input));
    
    // Layer 6: Simulation Performance
    findings.push(...this.diagnosePerformance(input));
    
    // Sort findings by severity and likelihood
    findings.sort((a, b) => {
      const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      const likelihoodOrder = { certain: 4, likely: 3, possible: 2, unlikely: 1 };
      
      const aScore = severityOrder[a.severity] * likelihoodOrder[a.likelihood];
      const bScore = severityOrder[b.severity] * likelihoodOrder[b.likelihood];
      
      return bScore - aScore;
    });
    
    return {
      findings,
      reproducerCode: this.generateReproducer(input, findings),
      patches: this.generatePatches(findings),
      tests: this.generateTests(findings),
      accessibilitySummary: this.generateAccessibilitySummary(findings),
      ciGuardrails: this.generateCIGuardrails(findings)
    };
  }
  
  private static diagnoseSimulationData(input: DiagnosticInput): DiagnosticFinding[] {
    const findings: DiagnosticFinding[] = [];
    const result = input.simulationResult as any;
    
    // Check Monte Carlo convergence
    if (input.simulationContext.iterations < 1000) {
      findings.push({
        layer: 'simulation-data',
        severity: 'high',
        likelihood: 'likely',
        evidence: `Only ${input.simulationContext.iterations} iterations configured`,
        impact: 'Statistical instability in percentile calculations, unreliable confidence bands',
        fix: 'Increase iterations to minimum 1000 for stable convergence',
        codeReference: 'simulationContext.iterations'
      });
    }
    
    // Verify percentile ordering
    const percentiles = result.timelinePercentiles?.P50 || result.percentiles;
    if (percentiles && Array.isArray(percentiles) && percentiles.length > 0) {
      const samplePoint = percentiles[0];
      if (typeof samplePoint === 'object' && samplePoint.p10 !== undefined) {
        const invalidOrdering = percentiles.some((point: any) => 
          point.p10 > point.p25 || point.p25 > point.p50 || 
          point.p50 > point.p75 || point.p75 > point.p90
        );
        
        if (invalidOrdering) {
          findings.push({
            layer: 'simulation-data',
            severity: 'critical',
            likelihood: 'certain',
            evidence: 'Percentile ordering violation detected in timeline data',
            impact: 'Charts display mathematically impossible confidence bands',
            fix: 'Validate percentile calculation in StatisticsCalculator.calculatePercentiles()',
            codeReference: 'StatisticsCalculator.calculatePercentiles'
          });
        }
      }
    }
    
    // Check timeline continuity
    const timelineData = result.timelineData || result.timeline;
    if (timelineData && Array.isArray(timelineData)) {
      const gaps = this.findTimelineGaps(timelineData);
      if (gaps.length > 0) {
        findings.push({
          layer: 'simulation-data',
          severity: 'medium',
          likelihood: 'likely',
          evidence: `Timeline gaps detected at years: ${gaps.join(', ')}`,
          impact: 'Broken line charts, missing retirement phase visualization',
          fix: 'Ensure continuous year sequence in timeline generation',
          codeReference: 'timelineData generation'
        });
      }
    }
    
    return findings;
  }
  
  private static diagnoseFinancialEncoding(input: DiagnosticInput): DiagnosticFinding[] {
    const findings: DiagnosticFinding[] = [];
    const result = input.simulationResult as any;
    
    // Check currency formatting consistency
    const hasInconsistentCurrency = this.checkCurrencyConsistency(result);
    if (hasInconsistentCurrency) {
      findings.push({
        layer: 'financial-encoding',
        severity: 'medium',
        likelihood: 'possible',
        evidence: 'Inconsistent currency formatting detected across data points',
        impact: 'Confusing financial displays, regulatory compliance issues',
        fix: 'Standardize currency formatting using Intl.NumberFormat with AUD locale',
        codeReference: 'currency formatting functions'
      });
    }
    
    // Validate confidence band accuracy
    const percentileData = result.timelinePercentiles;
    if (percentileData && input.chartStack.chartType === 'fan') {
      const bandAccuracyIssues = this.validateConfidenceBands(percentileData);
      if (bandAccuracyIssues.length > 0) {
        findings.push({
          layer: 'financial-encoding',
          severity: 'high',
          likelihood: 'likely',
          evidence: `Confidence band issues: ${bandAccuracyIssues.join(', ')}`,
          impact: 'Misleading risk visualization, incorrect financial projections',
          fix: 'Recalibrate confidence band rendering with proper opacity gradients',
          codeReference: 'FanChart confidence band rendering'
        });
      }
    }
    
    return findings;
  }
  
  private static diagnoseTemporalAxes(input: DiagnosticInput): DiagnosticFinding[] {
    const findings: DiagnosticFinding[] = [];
    const result = input.simulationResult as any;
    
    // Check age/year alignment
    const timelineData = result.timelineData || result.timeline;
    if (timelineData && Array.isArray(timelineData)) {
      const alignmentIssues = this.checkAgeYearAlignment(timelineData);
      if (alignmentIssues.length > 0) {
        findings.push({
          layer: 'temporal-axes',
          severity: 'medium',
          likelihood: 'likely',
          evidence: `Age/year misalignment detected: ${alignmentIssues.join(', ')}`,
          impact: 'Confusing timeline display, retirement phase markers incorrect',
          fix: 'Synchronize age and year calculations in timeline generation',
          codeReference: 'timeline age/year calculation'
        });
      }
    }
    
    return findings;
  }
  
  private static diagnoseInteractiveState(input: DiagnosticInput): DiagnosticFinding[] {
    const findings: DiagnosticFinding[] = [];
    
    // Check for state management issues (placeholder - would need actual component analysis)
    if (input.evidence.consoleLogs) {
      const stateErrors = input.evidence.consoleLogs.filter(log => 
        log.includes('state') && (log.includes('error') || log.includes('warning'))
      );
      
      if (stateErrors.length > 0) {
        findings.push({
          layer: 'interactive-state',
          severity: 'high',
          likelihood: 'likely',
          evidence: `State management errors detected: ${stateErrors.length} instances`,
          impact: 'Broken chart interactions, scenario parameter changes not reflected',
          fix: 'Debug React state updates and useEffect dependencies',
          codeReference: 'chart component state management'
        });
      }
    }
    
    return findings;
  }
  
  private static diagnoseAccessibility(input: DiagnosticInput): DiagnosticFinding[] {
    const findings: DiagnosticFinding[] = [];
    
    // Check accessibility audit results
    if (input.evidence.accessibilityAudit) {
      const violations = input.evidence.accessibilityAudit.violations || [];
      
      if (violations.length > 0) {
        findings.push({
          layer: 'accessibility',
          severity: 'high',
          likelihood: 'certain',
          evidence: `${violations.length} accessibility violations detected`,
          impact: 'Chart inaccessible to screen readers, WCAG compliance failure',
          fix: 'Add ARIA labels, alt text, and keyboard navigation support',
          codeReference: 'chart accessibility attributes'
        });
      }
    }
    
    return findings;
  }
  
  private static diagnosePerformance(input: DiagnosticInput): DiagnosticFinding[] {
    const findings: DiagnosticFinding[] = [];
    
    // Check performance metrics
    if (input.evidence.performanceMetrics) {
      const renderTime = input.evidence.performanceMetrics.renderTime;
      if (renderTime && renderTime > 2000) { // 2 seconds
        findings.push({
          layer: 'performance',
          severity: 'medium',
          likelihood: 'likely',
          evidence: `Slow render time: ${renderTime}ms`,
          impact: 'Poor user experience, unresponsive chart interactions',
          fix: 'Implement data downsampling and chart virtualization',
          codeReference: 'chart rendering optimization'
        });
      }
    }
    
    return findings;
  }
  
  // Helper methods
  private static findTimelineGaps(timelineData: any[]): number[] {
    const gaps: number[] = [];
    for (let i = 1; i < timelineData.length; i++) {
      const prevYear = timelineData[i - 1].year || timelineData[i - 1].age;
      const currYear = timelineData[i].year || timelineData[i].age;
      if (currYear - prevYear > 1) {
        gaps.push(prevYear + 1);
      }
    }
    return gaps;
  }
  
  private static checkCurrencyConsistency(result: any): boolean {
    // Simplified check - would need more sophisticated analysis
    const stringifiedResult = JSON.stringify(result);
    return stringifiedResult.includes('$') && stringifiedResult.includes('AUD');
  }
  
  private static validateConfidenceBands(percentileData: any): string[] {
    const issues: string[] = [];
    // Simplified validation - would need actual percentile analysis
    if (!percentileData.P10 || !percentileData.P90) {
      issues.push('Missing confidence band data');
    }
    return issues;
  }
  
  private static checkAgeYearAlignment(timelineData: any[]): string[] {
    const issues: string[] = [];
    // Simplified check - would need actual age/year validation
    const hasAgeField = timelineData.some(point => point.age !== undefined);
    const hasYearField = timelineData.some(point => point.year !== undefined);
    
    if (hasAgeField && hasYearField) {
      const misaligned = timelineData.some(point => 
        point.age && point.year && Math.abs((point.year - 2024) - (point.age - 35)) > 1
      );
      if (misaligned) {
        issues.push('Age and year fields inconsistent');
      }
    }
    
    return issues;
  }
  
  private static generateReproducer(input: DiagnosticInput, findings: DiagnosticFinding[]): string {
    return `
// Minimal Monte Carlo Chart Reproducer
import React from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts';

const MockSimulationData = ${JSON.stringify(input.simulationResult, null, 2).slice(0, 500)}...;

export const DiagnosticChart = () => {
  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={MockSimulationData.timelineData || []}>
        <XAxis dataKey="age" />
        <YAxis />
        <Line type="monotone" dataKey="p50" stroke="#8884d8" />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Issues to reproduce:
${findings.map(f => `// ${f.layer}: ${f.evidence}`).join('\n')}
    `;
  }
  
  private static generatePatches(findings: DiagnosticFinding[]): Array<{file: string, diff: string, description: string}> {
    return findings.map(finding => ({
      file: finding.codeReference || 'unknown',
      diff: `// Fix for ${finding.layer}: ${finding.fix}`,
      description: finding.impact
    }));
  }
  
  private static generateTests(findings: DiagnosticFinding[]): Array<{type: 'unit' | 'accessibility' | 'performance' | 'regression', code: string, description: string}> {
    return [
      {
        type: 'unit',
        code: `
test('percentile ordering validation', () => {
  const percentiles = { p10: 100, p25: 200, p50: 300, p75: 400, p90: 500 };
  expect(percentiles.p10 < percentiles.p25).toBe(true);
  expect(percentiles.p25 < percentiles.p50).toBe(true);
  expect(percentiles.p50 < percentiles.p75).toBe(true);
  expect(percentiles.p75 < percentiles.p90).toBe(true);
});
        `,
        description: 'Validate Monte Carlo percentile calculation accuracy'
      },
      {
        type: 'accessibility',
        code: `
test('chart accessibility requirements', async () => {
  const { container } = render(<MonteCarloChart />);
  const chart = container.querySelector('[role="img"]');
  expect(chart).toHaveAttribute('aria-label');
  expect(chart).toHaveAttribute('alt');
});
        `,
        description: 'Ensure WCAG compliance for financial charts'
      }
    ];
  }
  
  private static generateAccessibilitySummary(findings: DiagnosticFinding[]): string {
    const accessibilityIssues = findings.filter(f => f.layer === 'accessibility');
    if (accessibilityIssues.length === 0) {
      return 'Chart meets basic accessibility requirements for financial data visualization.';
    }
    
    return `Accessibility improvements needed: ${accessibilityIssues.map(f => f.fix).join('; ')}. These changes will ensure the Monte Carlo projections are accessible to users with disabilities and meet WCAG 2.1 AA standards.`;
  }
  
  private static generateCIGuardrails(findings: DiagnosticFinding[]): string[] {
    return [
      'Validate percentile ordering in all Monte Carlo outputs',
      'Check timeline continuity before chart rendering',
      'Verify currency formatting consistency',
      'Run accessibility audits on chart components',
      'Monitor chart rendering performance metrics',
      'Validate simulation convergence criteria'
    ];
  }
}